import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { OptimizelyFeatureFlag, PosthogFeatureFlag } from '@app/shared/interfaces';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const TIMER_DURATION = 1000;

/**
 * Service agnostic feature flag guard
 * Specify attributes via the route data object
 * Defaults to use posthog service
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagEnabledGuard  {
  constructor(
    private readonly optimizely: OptimizelyService,
    private readonly posthog: PosthogService,
    private readonly router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const { data } = route;

    if ((!data?.flag && !data?.permanent) || !data?.redirectTo) {
      return true;
    }
    const { flag, redirectTo, inverse, overrideKey, permanent, service = 'posthog' } = data;
    const modifiedRedirectTo = redirectTo.replace(/:(\w+)/g, (match, paramName) => {
      return route.params[paramName] || match;
    });
    const destination = this.router.createUrlTree([modifiedRedirectTo]);
    const override = localStorage.getItem(overrideKey);

    // When we're done with a flag on a route, we can redirect to the destination long term without the flag reference
    if (permanent) {
      return destination;
    }

    return this.enabled$(flag, service).pipe(
      map((enabled) => {
        if (inverse) {
          return enabled && override !== 'true' ? destination : true;
        }
        return enabled && override !== 'true' ? true : destination;
      })
    );
  }

  private enabled$(flag: OptimizelyFeatureFlag | PosthogFeatureFlag, service: 'posthog' | 'optimizely' = 'posthog') {
    return service === 'posthog'
      ? this.posthog.featureEnabled$(flag as PosthogFeatureFlag, TIMER_DURATION)
      : this.optimizely.featureEnabled$(flag as OptimizelyFeatureFlag, TIMER_DURATION);
  }
}
